import { Component, EventEmitter, Input, OnInit, Output, OnDestroy } from '@angular/core';

import { translate, translateObject } from '@jsverse/transloco';
import { Subscription } from 'rxjs';

import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { WidgetDataSource } from '../../../../models';
import { Widget } from '../../../../providers/bridge-api/dashboard.service';
import { StorageAccountService } from '../../../../providers/storage-api/blender-account.service';
import { FieldService } from '../../../../providers/storage-api/field.service';
import {
  AnalyticItemType,
  AnalyticItemTypeMap,
} from '../../../dashboard/services/analytic-item-types';
import { DashboardValidityChecks } from '../../../dashboard/services/validity-checks';
import { CrosstabWidgetOptions } from '../widget-crosstab/widget-crosstab.component';
import { AbstractChartReport } from '../../services/chart-report/chart-report-abstract/chart-report-abstract';

export interface AnalyticItemsDialogOptions {
  widget: Widget<CrosstabWidgetOptions>;
  widgetDataSource: WidgetDataSource;
  report: AbstractChartReport;
  analyticItemTypes: AnalyticItemTypeMap;
  resolveFn?: (result?) => any;
}

export interface RadioOption {
  name: string;
  label: string;
  tooltip: string;
  value: any;
  disabled: boolean;
  visible: boolean;
  checked: boolean;
}

@EnableClassTelemetry()
@Component({
  selector: 'app-analytic-item-selector',
  templateUrl: './analytic-item-selector.component.html',
  styleUrls: ['./analytic-item-selector.component.scss'],
  standalone: false,
})
export class AnalyticItemSelectorComponent implements OnInit, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @Input() widget: Widget;
  @Input() widgetDataSource: WidgetDataSource;
  @Input() report: AbstractChartReport;
  @Input() analyticItemTypes: AnalyticItemTypeMap;

  @Input() analyticItem: string;
  @Input() subtypes: string[];
  @Input() customFieldSelected: boolean;
  @Input() standardFieldSelected: boolean;
  @Input() sectionPropertySelected: boolean;
  @Input() isRowSelector: boolean;

  @Output() analyticItemChange = new EventEmitter<{ item: string; tooltip: string }>();
  @Output() subtypesChange = new EventEmitter<string[]>();
  @Output() customFieldSelectedChange = new EventEmitter<boolean>();
  @Output() standardFieldSelectedChange = new EventEmitter<boolean>();
  @Output() sectionPropertySelectedChange = new EventEmitter<boolean>();

  public analyticItemRadios: RadioOption[] = [];
  public subtypeRadios: RadioOption[] = [];

  private loaderInterval: number;

  public isBlankSelected = false;
  public isBlankEnabled = false;
  public disableBlankSelector = false;
  public disabledBlankTooltip = '';
  public selectedAnalyticItem: AnalyticItemType;
  public subtypeSelectionMode: 'none' | 'single' | 'multiple' = 'none';

  private someSubtypesSelected: boolean;
  private analyticItemTypeKeys: string[];
  private validityCache = new Map<
    string,
    { disabled: boolean; visible?: boolean; tooltip: string }
  >();

  private groupFieldsSub$: Subscription;
  private fieldTypeMap = new Map<string, string>();

  floatMetadataFieldNames: string[] = [];
  keywordMetadataFieldNames: string[] = [];
  constructor(
    private accountService: StorageAccountService,
    private fieldService: FieldService
  ) {}

  ngOnInit() {
    this.groupFieldsSub$ = this.accountService.groupFieldsSub.subscribe((accountMetadata) => {
      accountMetadata.metadata
        .filter((f) => f.metadata?.display !== false)
        .forEach((f) => this.fieldTypeMap.set(f.fieldName, f.fieldType));

      this.analyticItem = this.selfOrWidget('analyticItem');
      this.subtypes =
        this.subtypes ||
        (this.widget.payload.entityTypeFilter
          ? this.widget.payload.entityTypeFilter.split(',')
          : []);
      this.standardFieldSelected = this.selfOrWidget('standardFieldSelected');
      this.customFieldSelected = this.selfOrWidget('customFieldSelected');
      this.sectionPropertySelected = this.selfOrWidget('sectionPropertySelected');
      this.analyticItemTypeKeys = Object.keys(this.analyticItemTypes);

      this.isBlankSelected = this.widget.payload.showBlanks;
      this.isBlankEnabled =
        this.widget.payload.type === 'documentCount' &&
        (this.widget.payload.aggregator === 'total' ||
          this.widget.payload.aggregator === 'percentage');

      if (this.standardFieldSelected) {
        this.selectedAnalyticItem = this.analyticItemTypes.standardFields;
        this.subtypes = [this.analyticItem];
      } else if (this.customFieldSelected) {
        this.selectedAnalyticItem = this.analyticItemTypes.metadata;
        this.subtypes = [this.analyticItem];
      } else if (this.sectionPropertySelected) {
        this.selectedAnalyticItem = this.analyticItemTypes.sectionProperties;
        this.subtypes = [this.analyticItem];
      } else {
        this.selectedAnalyticItem = this.analyticItemTypes[this.analyticItem];
      }
      if (this.selectedAnalyticItem?.subTypes) {
        this.someSubtypesSelected = this.subtypes.length > 0;

        // Monkey-patch around DASH-4737
        if (this.selectedAnalyticItem.subTypes.length && !this.subtypes.length) {
          this.subtypes = [...this.selectedAnalyticItem.subTypes];
        }
      }

      this.subtypeSelectionMode = this.adjustSelectionMode();
      this.setupAnalyticItemRadios();
      this.setupSubtypeRadios();

      this.emitSelection();
    });
  }

  ngOnDestroy(): void {
    this.groupFieldsSub$.unsubscribe();
  }

  private async setupAnalyticItemRadios() {
    const fields = await this.fieldService.getFields();
    this.floatMetadataFieldNames = fields.items
      .filter((f) => f.fieldType === 'double')
      .map((f) => f.fieldName);
    this.keywordMetadataFieldNames = fields.items
      .filter((f) => f.fieldType === 'keyword')
      .map((f) => f.fieldName);
    this.analyticItemRadios = [];
    this.handleIncludeBlanks(this.analyticItem);

    const name = this.isRowSelector ? 'rowAnalyticItemRadio' : 'analyticItemRadio';
    for (const key of this.analyticItemTypeKeys) {
      this.fillValidityCacheFor(this.analyticItemTypes[key]);
      const cachedItem = this.validityCache.get(this.analyticItemTypes[key].analyticItem);
      this.analyticItemRadios.push({
        name,
        label: this.analyticItemTypes[key].displayName,
        tooltip: cachedItem.tooltip,
        value: this.analyticItemTypes[key],
        disabled: cachedItem.disabled,
        visible: true,
        checked: this.analyticItemTypes[key] === this.selectedAnalyticItem,
      });
    }

    if (!this.selectedAnalyticItem) {
      const getFirstAvailableRadioKey = () => {
        for (const [key, value] of this.validityCache.entries()) {
          if (value.disabled || !this.analyticItemTypeKeys.includes(key)) {
            continue;
          }
          return key;
        }
      };
      const key = getFirstAvailableRadioKey();
      this.analyticItemRadios = this.analyticItemRadios.map((radio) => {
        if (radio.value.analyticItem !== key) {
          return radio;
        }
        return { ...radio, checked: true };
      });
    }
  }

  // Interval trick to make angular's slow render cancelable (DASH-5282)
  private setupSubtypeRadios() {
    clearInterval(this.loaderInterval);
    this.fillValidityCacheFor(this.selectedAnalyticItem);
    this.subtypeRadios = [];
    const isMetadata = this.selectedAnalyticItem?.analyticItem === 'metadata';
    const isSectionProperty = this.selectedAnalyticItem?.analyticItem === 'sectionProperties';
    const selections = this.selections ?? [];
    const loadSize = 25;
    let ctr = 0;

    const isFloatMetadataType = (fieldName) => {
      if (
        this.widget.payload.type !== 'bigNumber' &&
        this.widget.payload.type !== 'numericMetadata' &&
        isMetadata
      ) {
        return this.floatMetadataFieldNames.includes(fieldName);
      }
    };

    const getRadioLabel = (radio) => {
      if (isMetadata) {
        return FieldService.getFieldLongName(radio);
      }
      if (this.selectedAnalyticItem?.analyticItem === 'topics') {
        return translate(`analytic-items.topics-subtypes.${radio.toLocaleLowerCase()}`);
      }
      // Handle section properties with translation keys
      if (isSectionProperty && radio.startsWith('sectionProperties.')) {
        const propertyName = radio.substring('sectionProperties.'.length).toLowerCase();
        return translate(`analytic-items.sectionproperties.${propertyName}`);
      }
      return radio;
    };

    const loadNext = () => {
      const max = Math.min(selections.length, ctr + loadSize);
      const name = this.isRowSelector ? 'rowSubtypeRadio' : 'columnSubtypeRadio';
      for (let i = ctr; i < max; i++) {
        const radio = selections[i];
        const cachedItem = this.validityCache.get(radio);
        this.subtypeRadios.push({
          name,
          label: getRadioLabel(radio),
          tooltip: cachedItem.tooltip,
          value: radio,
          disabled: isFloatMetadataType(radio) || cachedItem.disabled,
          visible: cachedItem.visible,
          checked:
            isMetadata || this.standardFieldSelected || isSectionProperty
              ? this.subtypes[0] === radio
              : !this.subtypes.includes(radio),
        });
      }

      if (selections.length < ctr + loadSize) {
        this.subtypeRadios.sort((a, b) =>
          a.label.localeCompare(b.label, undefined, { sensitivity: 'accent' })
        );
        clearInterval(this.loaderInterval);
      } else {
        ctr = max;
      }
    };

    this.loaderInterval = <any>setInterval(() => loadNext(), 300);
  }

  private selfOrWidget(prop: string) {
    return this[prop] === undefined ? this.widget.payload[prop] : this[prop];
  }

  private adjustSelectionMode() {
    let selectionMode;
    if (this.standardFieldSelected || this.customFieldSelected || this.sectionPropertySelected) {
      selectionMode = 'single';
    } else if (
      (this.selectedAnalyticItem?.subTypes && this.selectedAnalyticItem.subTypes.length) ||
      this.selectedAnalyticItem?.taxonomyOptions
    ) {
      selectionMode = 'multiple';
    } else {
      selectionMode = 'none';
    }

    return selectionMode;
  }

  public isAnalyticSubTypeVisible(analyticItemType: AnalyticItemType, subType: string) {
    this.fillValidityCacheFor(analyticItemType);

    return this.validityCache.get(subType).visible;
  }

  private fillValidityCacheFor(analyticItemType: AnalyticItemType) {
    if (!analyticItemType) {
      return;
    }
    if (this.validityCache.has(analyticItemType.analyticItem)) {
      return;
    }

    let label: string;
    let result: any = {
      disabled: false,
    };

    const translations = translateObject('widgets.analytic-item-selector');

    if (analyticItemType.analyticItem === 'metadata') {
      label = translations['no-selectable-custom-fields'];
      if (!this.analyticItemTypes.metadata.subTypes?.length) {
        result.disabled = true;
        result.tooltip = translations['no-custom-fields'];
      } else if (
        this.widget.payload.type !== 'bigNumber' &&
        this.widget.payload.type !== 'numericMetadata' &&
        this.analyticItemTypes.metadata.subTypes.filter(
          (s) => !this.floatMetadataFieldNames.includes(s)
        ).length === 0
      ) {
        result.disabled = true;
        result.tooltip = translations['no-applicable-custom-fields'];
      }
    } else if (analyticItemType.analyticItem === 'standard Fields') {
      label = translations['no-selectable-standard-fields'];
      if (
        !this.analyticItemTypes.standardFields.subTypes?.length ||
        this.widgetDataSource.isSocial
      ) {
        result.disabled = true;
        result.tooltip = translations['no-standard-fields'];
      }
    } else if (analyticItemType.analyticItem === 'sectionProperties') {
      label = translations['no-selectable-section-properties'];
      if (!this.analyticItemTypes.sectionProperties.subTypes?.length) {
        result.disabled = true;
        result.tooltip = translations['no-section-properties'];
      }
      if (
        [
          'featureSentimentScoreOverTime',
          'featureSentimentScore',
          'documentSentimentPolarity',
          'documentSentimentPolarityTopFeatures',
        ].includes(this.report.reportName) &&
        !this.widgetDataSource.hasSectionSentiment
      ) {
        result.disabled = true;
        result.tooltip = translations['no-section-sentiment'];
      }
    } else {
      label = translations['no-selectable-subtypes'];
      result = DashboardValidityChecks.analyticTypeValidCheck(
        analyticItemType.analyticItem,
        this.widget,
        this.widgetDataSource,
        this.report
      );
    }

    this.assembleValidityObjectFromSubTypes(analyticItemType, result, label);
    this.validityCache.set(analyticItemType.analyticItem, result);
  }

  private assembleValidityObjectFromSubTypes(
    analyticItemType: AnalyticItemType,
    result,
    disableReason
  ) {
    if (result.disabled) {
      return;
    }

    result.disabled = this.fillValidityCacheForSubTypes(analyticItemType);
    result.tooltip = result.disabled ? disableReason : undefined;
  }

  private fillValidityCacheForSubTypes(analyticItemType: AnalyticItemType) {
    if (!analyticItemType.subTypes && !analyticItemType.taxonomyOptions) {
      return false;
    }

    let allDisabled = true;

    const subTypes = analyticItemType.subTypes || analyticItemType.taxonomyOptions || [];
    for (const subType of subTypes) {
      const subTypeResult = DashboardValidityChecks.analyticSubTypeValidCheck(
        analyticItemType.analyticItem,
        subType,
        this.fieldTypeMap,
        this.widget
      );
      this.validityCache.set(subType, { ...subTypeResult, visible: !subTypeResult.disabled });

      allDisabled &&= subTypeResult.disabled;
    }

    return allDisabled;
  }

  setAnalyticType(analyticItemObj: AnalyticItemType) {
    const { analyticItem, displayName, subTypes, taxonomyOptions } = analyticItemObj;
    if (
      this.analyticItem === analyticItem &&
      this.selectedAnalyticItem.displayName === displayName
    ) {
      return;
    }

    this.selectedAnalyticItem = analyticItemObj;
    this.analyticItem = analyticItem;
    this.subtypes = [];
    this.someSubtypesSelected = false;
    this.handleIncludeBlanks(this.analyticItem);
    if (
      analyticItem === 'metadata' ||
      analyticItem === 'standard Fields' ||
      analyticItem === 'sectionProperties'
    ) {
      let filteredSubTypes = subTypes;
      if (
        this.widget.payload.type !== 'bigNumber' &&
        this.widget.payload.type !== 'numericMetadata' &&
        analyticItem === 'metadata'
      ) {
        filteredSubTypes = subTypes.filter((f) => !this.floatMetadataFieldNames.includes(f));
      }

      const visibleSubType = filteredSubTypes.find((subType) =>
        this.isAnalyticSubTypeVisible(analyticItemObj, subType)
      );
      this.subtypes.push(visibleSubType);
      this.handleIncludeBlanks(visibleSubType);
      this.emitSelection(visibleSubType);
    } else {
      this.subtypes = [];
      if (taxonomyOptions) {
        this.subtypes = [...taxonomyOptions];
      } else if (subTypes) {
        this.subtypes = [...subTypes];
      }
      this.emitSelection();
    }

    this.subtypeSelectionMode = this.adjustSelectionMode();
    this.setupSubtypeRadios();
  }

  setAnalyticSubType(checked: boolean, subtype) {
    if (this.subtypeSelectionMode === 'multiple') {
      if (!checked) {
        this.subtypes.push(subtype);
      } else {
        this.subtypes.splice(this.subtypes.indexOf(subtype), 1);
      }
      this.someSubtypesSelected = this.subtypes.length > 0;
    } else {
      this.subtypes = [subtype];
    }
    this.handleIncludeBlanks(subtype);
    this.emitSelection(subtype);
  }

  private handleIncludeBlanks(analyticItem) {
    const isKeywordSelected = this.keywordMetadataFieldNames.includes(analyticItem);
    if (isKeywordSelected) {
      //Have legacy blank values for keywords show up
      this.isBlankSelected = true;
    }

    this.disableBlankSelector = isKeywordSelected;
    this.disabledBlankTooltip = this.disableBlankSelector
      ? translate('analytic-items.include-blanks-disabled')
      : '';
  }

  private emitSelection(subtype?: string) {
    if (!this.analyticItem || !this.selectedAnalyticItem) {
      return;
    }
    this.standardFieldSelected = false;
    this.customFieldSelected = false;
    this.sectionPropertySelected = false;

    let analyticItemToEmit = this.analyticItem;
    let subtypeToEmit = [...this.subtypes];

    if (this.selectedAnalyticItem?.analyticItem === 'metadata') {
      analyticItemToEmit = subtype || subtypeToEmit[0];
      subtypeToEmit = [];
      this.customFieldSelected = true;
    } else if (this.selectedAnalyticItem?.analyticItem === 'standard Fields') {
      analyticItemToEmit = subtype || subtypeToEmit[0];
      subtypeToEmit = [];
      this.standardFieldSelected = true;
    } else if (this.selectedAnalyticItem?.analyticItem === 'sectionProperties') {
      analyticItemToEmit = subtype || subtypeToEmit[0];
      subtypeToEmit = [];
      this.sectionPropertySelected = true;
    }

    const tooltip = this.getInvalidTooltip(analyticItemToEmit);
    this.analyticItemChange.emit({ item: analyticItemToEmit, tooltip });
    this.subtypesChange.emit(subtypeToEmit);
    this.customFieldSelectedChange.emit(this.customFieldSelected);
    this.standardFieldSelectedChange.emit(this.standardFieldSelected);
    this.sectionPropertySelectedChange.emit(this.sectionPropertySelected);
  }

  private getInvalidTooltip(item: string): string {
    if (item === 'taxonomy') {
      return translate('widgets.analytic-item-selector.cannot-exclude-all-taxonomy');
    }
    if (item === 'entities') {
      return translate('widgets.analytic-item-selector.cannot-exclude-all-entity');
    }

    return translate('widgets.analytic-item-selector.cannot-exclude-all', { item });
  }

  get selections() {
    return this.selectedAnalyticItem?.taxonomyOptions || this.selectedAnalyticItem?.subTypes;
  }

  public get hasSelection(): boolean {
    let ret = !!this.selectedAnalyticItem;
    if (this.someSubtypesSelected) {
      ret = true;
    } else if (
      ret &&
      (this.selectedAnalyticItem.subTypes || this.selectedAnalyticItem.taxonomyOptions)
    ) {
      ret = !!this.subtypes.length;
    }

    return ret;
  }
}
